/**
 * `createSkaileApp` — the thin app SDK that makes an embedded app
 * agent-controllable.
 *
 * An app becomes a *capability peer*, mirroring how the runner behaves toward
 * the platform:
 *
 *  1. Opens an authenticated back-connection (via an {@link AppTransport}).
 *  2. Performs the app handshake — sends `app_hello`, awaits `app_hello_ack`
 *     (binding the connection to `session + appId`) or `app_hello_nack` (refusal).
 *  3. Registers its `app.*` capabilities via `capability_register`, stamping
 *     each with `origin:{kind:'app', appId}` and `side:'app'`. Register /
 *     deregister mid-session is supported.
 *  4. Answers inbound `capability_invoked` events by running the local handler
 *     and replying with `capability_result` (a thrown handler rides back as
 *     `{ error }`).
 *
 * The approval round-trip for `requiresApproval` capabilities is handled
 * entirely platform-side: the gateway parks the invocation for user approval
 * and only forwards `capability_invoked` to the app *after* approval, so the
 * app just marks the capability and answers when asked.
 *
 * The peer reconnects and replays its registrations on a dropped socket, and on
 * a *retryable* refusal — the app boots independently of the session's agent
 * container and can win that race. See {@link ReconnectOptions}.
 *
 * @category App SDK
 * @since 3.4.0
 */

import type { Capability } from "@skaile/workspaces/types";
import { PROTOCOL_VERSION_STRING } from "@skaile/workspaces/types";
import type { DefinedAppCapability } from "./define-app-capability.js";
import { type AppLogger, noopLogger } from "./logger.js";
import type { AppServerMessage, AppTransport } from "./transport.js";

/** Default timeout waiting for the gateway's `app_hello_ack`. */
export const DEFAULT_HELLO_TIMEOUT_MS = 10_000;

/** Defaults mirroring the agent `WebSocketClientTransport`'s backoff. */
const DEFAULT_RECONNECT: Required<ReconnectOptions> = {
  maxAttempts: 5,
  baseMs: 1_000,
  maxMs: 30_000,
};

/**
 * Reconnect / retry policy. Set `reconnect: false` on {@link SkaileAppOptions}
 * to disable entirely (one attempt, no replay).
 *
 * @category App SDK
 * @since 3.5.0
 */
export interface ReconnectOptions {
  /** Attempts before giving up. Default 5. */
  maxAttempts?: number;
  /** First backoff delay in ms; doubles each attempt. Default 1000. */
  baseMs?: number;
  /** Backoff ceiling in ms. Default 30000. */
  maxMs?: number;
}

/**
 * The gateway refused the handshake. `retryable` mirrors the wire field: `true`
 * for a transient refusal (the agent container is not up yet), `false` for a
 * permanent one (bad token, `appId` not permitted).
 *
 * @category App SDK
 * @since 3.5.0
 */
export class AppHandshakeRefusedError extends Error {
  readonly retryable: boolean;
  constructor(reason: string, retryable: boolean) {
    super(`app handshake refused: ${reason}`);
    this.name = "AppHandshakeRefusedError";
    this.retryable = retryable;
  }
}

/**
 * Options for {@link createSkaileApp}.
 *
 * @category App SDK
 * @since 3.4.0
 */
export interface SkaileAppOptions {
  /** The embedded app's stable id. Every registered capability is bound to it. */
  appId: string;
  /**
   * The app back-connection. Inject {@link createWebSocketAppTransport} in
   * production or a fake in tests. The transport carries the injected session
   * identity as its connection credential.
   *
   * With `reconnect` enabled the transport must be able to produce a **fresh**
   * token per attempt — the gateway consumes it at `app_hello`. Pass a
   * `SessionTokenProvider` rather than a bare string.
   */
  transport: AppTransport;
  /** Protocol version the SDK announces in `app_hello`. Defaults to the SDK's. */
  protocolVersion?: string;
  /** Timeout waiting for `app_hello_ack`. Defaults to {@link DEFAULT_HELLO_TIMEOUT_MS}. */
  helloTimeoutMs?: number;
  /** Logger for the SDK and (by default) capability handlers. Defaults to {@link noopLogger}. */
  logger?: AppLogger;
  /**
   * Reconnect policy, or `false` to disable. Defaults to
   * `{maxAttempts:5, baseMs:1000, maxMs:30000}`.
   *
   * @since 3.5.0
   */
  reconnect?: ReconnectOptions | false;
}

/** Result of a successful handshake — the bound session context. */
export interface AppSession {
  sessionId: string;
  userId?: string;
}

/**
 * A connected (or connectable) Skaile app. Register capabilities before or
 * after {@link connect}; post-connect changes are pushed to the gateway
 * immediately.
 *
 * @category App SDK
 * @since 3.4.0
 */
export interface SkaileApp {
  /**
   * Add (or replace, by name) an app capability. Registering the same name
   * again replaces it — mirrors the registry's replace-by-name semantics. When
   * already connected, a `capability_register` is sent immediately.
   */
  registerCapability(cap: DefinedAppCapability): void;
  /**
   * Remove a capability by name. No-op when unknown. When connected, a
   * `capability_deregister` is sent immediately so the tool disappears from the
   * agent's catalog.
   */
  deregisterCapability(name: string): void;
  /** Wire-format snapshot of the currently registered capabilities (origin stamped). */
  readonly capabilities: readonly Capability[];
  /** The bound session once connected; `null` before the handshake and while reconnecting. */
  readonly session: AppSession | null;
  /**
   * Open the connection, perform the handshake, and register the current
   * capabilities. Resolves with the bound {@link AppSession}. Rejects if the
   * gateway refuses permanently, the handshake times out, or the transport
   * disconnects mid-handshake. A *retryable* refusal is retried per the
   * reconnect policy before rejecting.
   */
  connect(): Promise<AppSession>;
  /**
   * Subscribe to session binding changes. Fires on **every** bind — including
   * the initial `connect()` — with the new {@link AppSession}, and with `null`
   * whenever the connection drops. Returns an unsubscribe function.
   *
   * @since 3.5.0
   */
  onSessionChange(handler: (session: AppSession | null) => void): () => void;
  /** Disconnect and tear down. Idempotent. Cancels any pending reconnect. */
  close(): Promise<void>;
}

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Create a {@link SkaileApp}. See the module doc for the peer lifecycle.
 *
 * @category App SDK
 * @since 3.4.0
 */
export function createSkaileApp(options: SkaileAppOptions): SkaileApp {
  const {
    appId,
    transport,
    protocolVersion = PROTOCOL_VERSION_STRING,
    helloTimeoutMs = DEFAULT_HELLO_TIMEOUT_MS,
    logger = noopLogger,
  } = options;

  const reconnectEnabled = options.reconnect !== false;
  const policy: Required<ReconnectOptions> = {
    ...DEFAULT_RECONNECT,
    ...(options.reconnect === false ? {} : options.reconnect),
  };

  const registry = new Map<string, DefinedAppCapability>();
  const sessionListeners = new Set<(s: AppSession | null) => void>();
  let session: AppSession | null = null;
  let closed = false;
  let connecting = false;
  let subscribed = false;
  // Bumped by close(). A reconnect loop parked on its backoff timer captures the
  // epoch it started in and abandons if close() (or a close/connect cycle) has
  // since happened — otherwise it would resurrect a torn-down app.
  let epoch = 0;
  let unsubscribeMessage: (() => void) | null = null;
  let unsubscribeDisconnect: (() => void) | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  /** Resolver of the backoff sleep, so close() can wake a parked loop. */
  let reconnectWake: (() => void) | null = null;

  /** Non-null only while a handshake is in flight. */
  let pending: { settle: (err: Error | null, s?: AppSession) => void } | null = null;

  function toWire(cap: DefinedAppCapability): Capability {
    return {
      name: cap.name,
      displayName: cap.displayName,
      description: cap.description,
      inputSchema: cap.inputSchema,
      outputSchema: cap.outputSchema,
      side: "app",
      origin: { kind: "app", appId },
      scope: cap.scope,
      requiresApproval: cap.requiresApproval,
      kind: cap.kind,
      audience: cap.audience,
      promptFragment: cap.promptFragment,
      callTimeoutMs: cap.callTimeoutMs,
      render: cap.render,
    };
  }

  function setSession(next: AppSession | null): void {
    session = next;
    for (const h of sessionListeners) h(next);
  }

  function registerCapability(cap: DefinedAppCapability): void {
    registry.set(cap.name, cap);
    if (session) {
      transport.send({ type: "capability_register", capabilities: [toWire(cap)] });
      logger.debug("registered capability (live)", { name: cap.name });
    }
  }

  function deregisterCapability(name: string): void {
    if (!registry.delete(name)) return;
    if (session) {
      transport.send({ type: "capability_deregister", names: [name] });
      logger.debug("deregistered capability (live)", { name });
    }
  }

  /** Dispatch an inbound invocation to the local handler; reply with the result. */
  async function handleInvocation(callId: string, name: string, input: unknown): Promise<void> {
    const cap = registry.get(name);
    if (!cap) {
      transport.send({
        type: "capability_result",
        callId,
        result: { error: `Unknown app capability: ${name}` },
      });
      return;
    }
    try {
      const validated = cap.inputZod.parse(input);
      const ctx = { sessionId: session?.sessionId ?? "", appId, log: logger };
      const result = await cap.handler(validated, ctx);
      transport.send({ type: "capability_result", callId, result });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      logger.warn("capability handler failed", { name, error: message });
      transport.send({ type: "capability_result", callId, result: { error: message } });
    }
  }

  function onMessage(message: AppServerMessage): void {
    switch (message.type) {
      case "app_hello_ack": {
        const bound: AppSession = { sessionId: message.sessionId, userId: message.userId };
        pending?.settle(null, bound);
        return;
      }
      case "app_hello_nack":
        pending?.settle(new AppHandshakeRefusedError(message.error, message.retryable === true));
        return;
      case "capability_invoked":
        void handleInvocation(message.callId, message.name, message.input);
        return;
    }
  }

  function onDisconnect(): void {
    if (pending) {
      pending.settle(new Error("transport disconnected during handshake"));
      return;
    }
    if (closed || !session) return;
    logger.warn("app connection lost", { appId });
    setSession(null);
    if (!reconnectEnabled) return;
    // The loop holds the same guard connect() uses: a manual connect() while it
    // runs would start a second handshake, and both would overwrite `pending`.
    connecting = true;
    const loopEpoch = epoch;
    void reconnectLoop().finally(() => {
      // A close()/connect() cycle since this loop started owns the guard now.
      if (epoch === loopEpoch) connecting = false;
    });
  }

  function ensureSubscribed(): void {
    if (subscribed) return;
    unsubscribeMessage = transport.onMessage(onMessage);
    unsubscribeDisconnect = transport.onDisconnect(onDisconnect);
    subscribed = true;
  }

  /** One attempt: open the socket, exchange hello/ack, replay the registry. */
  function performHandshake(): Promise<AppSession> {
    ensureSubscribed();
    return transport.connect().then(
      () =>
        new Promise<AppSession>((resolve, reject) => {
          if (closed) {
            reject(new Error("app closed during handshake"));
            return;
          }
          let settled = false;
          const timer = setTimeout(() => {
            pending?.settle(new Error(`app handshake timed out after ${helloTimeoutMs}ms`));
          }, helloTimeoutMs);

          pending = {
            settle: (err, bound) => {
              if (settled) return;
              settled = true;
              clearTimeout(timer);
              pending = null;
              if (err || !bound) {
                reject(err ?? new Error("app handshake failed"));
                return;
              }
              setSession(bound);
              logger.info("app connection established", { appId, sessionId: bound.sessionId });
              // Replay the full surface — on a reconnect the gateway dropped our
              // caps when the previous connection closed.
              const wire = Array.from(registry.values()).map(toWire);
              if (wire.length > 0) {
                transport.send({ type: "capability_register", capabilities: wire });
              }
              resolve(bound);
            },
          };

          transport.send({ type: "app_hello", appId, protocolVersion });
        }),
      (err) => Promise.reject(err instanceof Error ? err : new Error(String(err))),
    );
  }

  const backoffMs = (attempt: number) =>
    Math.min(policy.maxMs, policy.baseMs * 2 ** Math.max(0, attempt - 1));

  /** Rebind after a dropped socket. Stops on a permanent refusal or close(). */
  async function reconnectLoop(): Promise<void> {
    const myEpoch = epoch;
    for (let attempt = 1; attempt <= policy.maxAttempts; attempt++) {
      const wait = backoffMs(attempt);
      logger.debug("scheduling app reconnect", { appId, attempt, waitMs: wait });
      // close() cancels the timer, so it also has to hand us the resolver —
      // otherwise this await never settles and the loop frame parks forever,
      // pinning the transport and every other captured binding.
      await new Promise<void>((r) => {
        reconnectWake = r;
        reconnectTimer = setTimeout(r, wait);
      });
      reconnectWake = null;
      if (closed || epoch !== myEpoch) {
        logger.debug("app reconnect cancelled", { appId, attempt });
        return;
      }
      try {
        await performHandshake();
        logger.info("app connection re-established", { appId });
        return;
      } catch (err) {
        if (err instanceof AppHandshakeRefusedError && !err.retryable) {
          logger.error("app reconnect refused permanently", { appId, error: err.message });
          return;
        }
        logger.warn("app reconnect attempt failed", {
          appId,
          attempt,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    logger.error("app reconnect gave up", { appId, attempts: policy.maxAttempts });
  }

  async function connect(): Promise<AppSession> {
    if (session) return session;
    if (connecting) throw new Error("app connect() is already in progress");
    connecting = true;
    // A closed app is reusable: connect() reopens it. Any reconnect loop still
    // parked from the previous life is fenced off by the epoch close() bumped.
    closed = false;
    try {
      for (let attempt = 1; ; attempt++) {
        try {
          return await performHandshake();
        } catch (err) {
          // Only a *retryable* refusal is worth another attempt on the initial
          // connect: the app raced the session's agent container on boot. Every
          // other failure (bad token, timeout, transport error) is terminal here.
          const transient = err instanceof AppHandshakeRefusedError && err.retryable;
          if (!transient || !reconnectEnabled || attempt >= policy.maxAttempts || closed) {
            throw err;
          }
          logger.debug("app handshake refused (retryable), retrying", { appId, attempt });
          await delay(backoffMs(attempt));
        }
      }
    } finally {
      connecting = false;
    }
  }

  async function close(): Promise<void> {
    closed = true;
    epoch++;
    clearTimeout(reconnectTimer);
    // Wake a loop parked on its backoff so it observes `closed` and returns,
    // rather than awaiting a timer that will now never fire.
    reconnectWake?.();
    reconnectWake = null;
    connecting = false;
    // Abort an in-flight handshake — rejects the pending connect and clears its
    // timer so nothing leaks past close().
    pending?.settle(new Error("app closed during handshake"));
    unsubscribeMessage?.();
    unsubscribeDisconnect?.();
    unsubscribeMessage = null;
    unsubscribeDisconnect = null;
    subscribed = false;
    session = null;
    await transport.disconnect();
  }

  return {
    registerCapability,
    deregisterCapability,
    get capabilities() {
      return Array.from(registry.values()).map(toWire);
    },
    get session() {
      return session;
    },
    connect,
    onSessionChange(handler) {
      sessionListeners.add(handler);
      return () => sessionListeners.delete(handler);
    },
    close,
  };
}
